import * as Sentry from '@sentry/react';
import { User } from '../types';
import { platformService } from './platform';
import { notificationsApi } from './api/notifications';

class NotificationService {
    private _pushListenersRegistered = false;
    private _setupPromise: Promise<void> | null = null;
    private _currentUserId: string | null = null;
    private readonly TOKEN_STORAGE_KEY = 'push_token_metadata';

    /**
     * Initialize push notifications listeners (call once on app start)
     */
    async setupPushListeners(): Promise<void> {
        if (!platformService.isNativeApp()) return;
        if (this._pushListenersRegistered) return;

        // Return existing promise if setup is already in progress
        if (this._setupPromise) {
            return this._setupPromise;
        }

        this._setupPromise = (async () => {
            try {
                const { PushNotifications } = await import('@capacitor/push-notifications');
                const { Preferences } = await import('@capacitor/preferences');

                // Listen for registration success
                await PushNotifications.addListener('registration', async (token) => {
                    console.log('✅ Push registration SUCCESS! Token:', token.value);
                    try {
                        const appVersion = await platformService.getAppVersion();
                        const deviceId = await platformService.getDeviceId();
                        const appType = await platformService.getAppType();
                        const deviceModel = await platformService.getDeviceModel();
                        const platform = await platformService.detectPlatform();
                        const apnsEnvironment = await platformService.getAPNsEnvironment();

                        // Check if token and metadata match what we last sent
                        const { value: storedDataStr } = await Preferences.get({ key: this.TOKEN_STORAGE_KEY });
                        if (storedDataStr) {
                            try {
                                const storedData = JSON.parse(storedDataStr);
                                if (
                                    storedData.token === token.value &&
                                    storedData.appVersion === appVersion &&
                                    storedData.appType === appType &&
                                    storedData.apnsEnvironment === apnsEnvironment &&
                                    storedData.userId === this._currentUserId
                                ) {
                                    console.log('ℹ️ Token and metadata already up to date on server, skipping update');
                                    return;
                                }
                            } catch {
                                // Ignore parse error, proceed to update
                            }
                        }

                        // Send token to server with environment info.
                        // updatePushToken swallows axios errors and returns { success: false }
                        // instead of throwing, so check the result explicitly — otherwise a
                        // rejected POST (e.g. 422/401) would silently look like success and we'd
                        // never learn why the token didn't land in the DB.
                        const updateResult = await notificationsApi.updatePushToken(platform, token.value, appVersion, deviceId, appType, deviceModel, apnsEnvironment);
                        if (!updateResult.success) {
                            throw new Error(updateResult.error || 'updatePushToken returned success:false');
                        }
                        console.log('✅ Token sent to server successfully with metadata:', { appVersion, deviceId, appType, deviceModel, apnsEnvironment });

                        // Update stored metadata
                        await Preferences.set({
                            key: this.TOKEN_STORAGE_KEY,
                            value: JSON.stringify({
                                token: token.value,
                                appVersion,
                                deviceId,
                                appType,
                                deviceModel,
                                apnsEnvironment,
                                userId: this._currentUserId,
                                lastUpdated: Date.now()
                            })
                        });
                    } catch (error) {
                        console.error('❌ Failed to send token to server:', error);
                        Sentry.captureException(error, {
                            tags: { error_type: 'push_token_sync' },
                        });
                    }
                });

                // Listen for registration error.
                // On iOS the token comes straight from APNs, but on Android it must be
                // minted by FCM (google-services.json + a Firebase project with Cloud
                // Messaging enabled, on a device with Google Play services). When that
                // setup is missing this fires and no token is ever sent to the server —
                // so tag the platform to make Android-only failures obvious.
                await PushNotifications.addListener('registrationError', (error) => {
                    console.error('❌ Push registration ERROR:', error);
                    Sentry.captureException(
                        new Error(error.error || 'Push notification registration failed'),
                        {
                            tags: {
                                error_type: 'push_registration',
                                platform: platformService.detectPlatform(),
                            },
                            extra: { registrationError: error },
                        },
                    );
                });

                // Listen for push notifications received
                await PushNotifications.addListener('pushNotificationReceived', (notification) => {
                    console.log('📱 Push notification received:', notification);
                });

                // Listen for notification action performed
                await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
                    console.log('🔔 Push notification action performed:', notification);
                    this.routeNotificationTap(notification?.notification?.data);
                });

                this._pushListenersRegistered = true;
                console.log('✅ Push listeners registered successfully');
            } catch (error) {
                console.error('❌ Failed to setup push listeners:', error);
                Sentry.captureException(error, {
                    tags: { error_type: 'push_setup' },
                });
                this._setupPromise = null; // Reset promise so we can try again
                throw error;
            }
        })();

        return this._setupPromise;
    }

    /**
     * Send the user where the push was about.
     *
     * A tap normally just resumes the app on whatever screen it was left on,
     * which is fine for a reminder and useless for a campaign: the post-exam
     * survey lives on the Hallouminati home screen, so a push asking "how did
     * it go?" that lands somebody on the vocabulary tab has asked a question
     * with no answer in sight.
     *
     * A hard navigation rather than a router push, deliberately: this runs
     * outside React, and reaching into the router from a singleton service is
     * how you get a listener holding a stale navigate() from a remounted tree.
     * A reload on a push tap is ordinary app behaviour.
     */
    private routeNotificationTap(data: Record<string, unknown> | undefined): void {
        try {
            const target = this.tapTarget(data);

            if (target === null || window.location.pathname === target) {
                return;
            }

            window.location.assign(target);
        } catch (error) {
            // Never let routing break the tap — worst case the app just opens.
            Sentry.captureException(error, { tags: { error_type: 'push_tap_routing' } });
        }
    }

    /** Known push actions, and the route each one means. */
    private tapTarget(data: Record<string, unknown> | undefined): string | null {
        switch (data?.action) {
            // The post-exam survey widget is the top block of the home screen.
            case 'open_home':
                return '/hallouminati';
            // A product announcement (announcement:send) opens the article it
            // announces in the guide reader.
            case 'open_guide_chapter': {
                const chapterId = typeof data?.chapter_id === 'string' ? data.chapter_id : '';
                return /^c\d+_\w+$/.test(chapterId) ? `/hallouminati/guide/chapter/${chapterId}` : '/hallouminati';
            }
            default:
                return null;
        }
    }

    /**
     * Register for push notifications (call after listeners are set up)
     */
    async registerForPushNotifications(): Promise<void> {
        if (!platformService.isNativeApp()) return;

        try {
            const { PushNotifications } = await import('@capacitor/push-notifications');

            // console.log('🔄 Attempting to register for push notifications...');
            await PushNotifications.register();
            // console.log('✅ Register call completed (waiting for token via listener)');
        } catch (error) {
            console.error('❌ Failed to register for push notifications:', error);
            Sentry.captureException(error, {
                tags: { error_type: 'push_registration' },
            });
            throw error;
        }
    }

    /**
     * Check permissions and register if appropriate
     */
    async checkAndSyncPushToken(user?: User): Promise<void> {
        if (!platformService.isNativeApp()) return;

        try {
            this._currentUserId = user?.id ? String(user.id) : null;

            // Ensure listeners are set up first
            await this.setupPushListeners();

            const { PushNotifications } = await import('@capacitor/push-notifications');
            const permStatus = await PushNotifications.checkPermissions();

            console.log('📋 Current push permission status:', permStatus.receive);

            // Only proceed if user wants notifications
            const userWantsNotifications = user?.reminder_frequency && user.reminder_frequency !== 'never';

            if (permStatus.receive === 'granted') {
                if (userWantsNotifications) {
                    console.log('✅ Permission granted, user wants notifications, registering...');
                    await this.registerForPushNotifications();
                } else {
                    console.log('ℹ️ Permission granted but user disabled notifications in settings');
                }
            } else if (permStatus.receive === 'prompt') {
                if (userWantsNotifications) {
                    console.log('🔔 Requesting push notification permission...');
                    const newStatus = await PushNotifications.requestPermissions();
                    console.log('📋 Permission request result:', newStatus.receive);

                    if (newStatus.receive === 'granted') {
                        await this.registerForPushNotifications();
                    }
                } else {
                    console.log('ℹ️ User has notifications disabled, not requesting permission');
                }
            } else if (permStatus.receive === 'denied') {
                console.log('❌ Push notifications denied by user - deactivating tokens on server');
                const { Preferences } = await import('@capacitor/preferences');
                const { value: storedDataStr } = await Preferences.get({ key: this.TOKEN_STORAGE_KEY });
                if (storedDataStr) {
                    const deviceId = await platformService.getDeviceId();
                    await notificationsApi.deactivatePushTokens(deviceId);
                    await Preferences.remove({ key: this.TOKEN_STORAGE_KEY });
                    console.log('✅ Tokens deactivated and local cache cleared');
                }
            }
        } catch (error) {
            console.error('❌ Failed to check/sync push token:', error);
            Sentry.captureException(error, {
                tags: { error_type: 'push_sync' },
            });
        }
    }

    /**
     * Check push notification permission
     */
    async checkPermission(): Promise<{ granted: boolean; denied: boolean; prompt: boolean }> {
        if (!platformService.isNativeApp()) {
            // Web: check browser notification API
            if ('Notification' in window) {
                return {
                    granted: Notification.permission === 'granted',
                    denied: Notification.permission === 'denied',
                    prompt: Notification.permission === 'default',
                };
            }
            return { granted: false, denied: false, prompt: false };
        }

        try {
            const { PushNotifications } = await import('@capacitor/push-notifications');
            const permStatus = await PushNotifications.checkPermissions();
            return {
                granted: permStatus.receive === 'granted',
                denied: permStatus.receive === 'denied',
                prompt: permStatus.receive === 'prompt',
            };
        } catch (error) {
            console.error('Failed to check push permissions:', error);
            Sentry.captureException(error, {
                tags: { error_type: 'push_permission_check' },
            });
            return { granted: false, denied: false, prompt: true };
        }
    }

    /**
     * Request push notification permission
     */
    async requestPermission(): Promise<boolean> {
        if (!platformService.isNativeApp()) {
            // Web: request browser notification permission
            if ('Notification' in window) {
                const permission = await Notification.requestPermission();
                return permission === 'granted';
            }
            return false;
        }

        try {
            // Setup listeners first
            await this.setupPushListeners();

            const { PushNotifications } = await import('@capacitor/push-notifications');
            const permStatus = await PushNotifications.requestPermissions();

            if (permStatus.receive === 'granted') {
                await this.registerForPushNotifications();
            }

            return permStatus.receive === 'granted';
        } catch (error) {
            console.error('Failed to request push permissions:', error);
            Sentry.captureException(error, {
                tags: { error_type: 'push_permission_request' },
            });
            return false;
        }
    }

    /**
     * Manually trigger push registration (useful for testing)
     */
    async forcePushRegistration(): Promise<void> {
        if (!platformService.isNativeApp()) {
            console.warn('⚠️ Cannot force push registration on non-native platform');
            return;
        }

        try {
            console.log('🔄 Forcing push registration...');
            await this.setupPushListeners();
            await this.registerForPushNotifications();
        } catch (error) {
            console.error('❌ Force registration failed:', error);
            Sentry.captureException(error, {
                tags: { error_type: 'push_force_registration' },
            });
        }
    }

    /**
     * Clear the badge count on the app icon
     */
    async clearBadgeCount(): Promise<void> {
        if (!platformService.isIOS()) {
            return; // Badge only relevant for iOS
        }

        try {
            const { PushNotifications } = await import('@capacitor/push-notifications');
            // Only attempt if device has been registered for push notifications
            const permStatus = await PushNotifications.checkPermissions();
            if (permStatus.receive !== 'granted') {
                return;
            }
            await PushNotifications.removeAllDeliveredNotifications();
            console.log('✅ Badge count cleared');
        } catch (error) {
            // Silently ignore "not registered" errors — expected for users who haven't registered
            const msg = error instanceof Error ? error.message : String(error);
            if (msg.includes('capacitorDidRegisterForRemoteNotifications')) {
                return;
            }
            console.warn('⚠️ Failed to clear badge count:', error);
            Sentry.captureException(error, {
                tags: { error_type: 'push_badge_clear' },
            });
        }
    }
}

export const notificationService = new NotificationService();
